import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Trash2, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminApi,
  type TagBatchRow,
  TAG_TYPES,
  type PhysicalTagType,
  type GenerateTagBatchOutput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Field, Select } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";

export const Route = createFileRoute("/_authed/tag-batches")({
  component: TagBatchesPage,
});

const PAGE_SIZE = 50;

function TagBatchesPage() {
  const qc = useQueryClient();
  const [count, setCount] = useState("100");
  const [type, setType] = useState<PhysicalTagType>("qr_sticker");
  const [notes, setNotes] = useState("");
  const [lastBatch, setLastBatch] = useState<GenerateTagBatchOutput | null>(null);

  const filters = useMemo(() => ({ limit: PAGE_SIZE }), []);

  const query = useInfiniteQuery({
    queryKey: queryKeys.tagBatches.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listTagBatches()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.batches) ?? [];

  const generate = useMutation({
    mutationFn: async () => {
      const n = parseInt(count, 10);
      if (!Number.isFinite(n) || n <= 0) throw new Error("Count must be a positive integer");
      const res = await adminApi.generateTagBatch()({ count: n, type, notes: notes || undefined });
      return res.data;
    },
    onSuccess: (data) => {
      setLastBatch(data);
      qc.invalidateQueries({ queryKey: queryKeys.tagBatches.list(filters) });
    },
  });

  const exportBatch = useMutation({
    mutationFn: async (batchId: string) => {
      const res = await adminApi.exportBatch()({ batchId });
      return res.data;
    },
    onSuccess: (data) => {
      downloadCsv(`${data.batchId}.csv`, data.rows, ["tagId", "url", "type", "status"]);
      qc.invalidateQueries({ queryKey: queryKeys.tagBatches.list(filters) });
    },
  });

  const revoke = useMutation({
    mutationFn: async (batchId: string) => {
      const res = await adminApi.revokeBatch()({ batchId, reason: "admin_revoke_batch" });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tagBatches.list(filters) });
      // Every tag in the batch just changed status too.
      qc.invalidateQueries({ queryKey: queryKeys.tags.all });
    },
  });

  // TanStack resets a mutation's own `error` on the next `mutate()` call, so
  // deriving from the mutations directly (instead of a separate `actionError`
  // state we'd have to wire onSuccess/onError for) can't go stale.
  const actionError = generate.error?.message ?? exportBatch.error?.message ?? revoke.error?.message ?? null;

  const columns = useMemo<ColumnDef<TagBatchRow, unknown>[]>(
    () => [
      {
        header: "Batch ID",
        accessorKey: "batchId",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.batchId}</span>
        ),
      },
      { header: "Type", accessorKey: "type" },
      {
        header: "Count",
        accessorKey: "count",
        cell: ({ row }) => <span className="font-mono">{row.original.count.toLocaleString()}</span>,
      },
      {
        header: "Inventory",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.statusCounts.inventory.toLocaleString()}
          </span>
        ),
      },
      {
        header: "Allocated",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {(row.original.statusCounts.unassigned + row.original.statusCounts.assigned).toLocaleString()}
          </span>
        ),
      },
      {
        header: "Revoked",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.statusCounts.revoked.toLocaleString()}
          </span>
        ),
      },
      {
        header: "Generated",
        cell: ({ row }) => (
          <span className="text-xs">
            {format(new Date(row.original.generatedAtMillis), "yyyy-MM-dd HH:mm")}
          </span>
        ),
      },
      {
        header: "Exported",
        cell: ({ row }) =>
          row.original.exportedAtMillis ? (
            <span className="text-xs">
              {format(new Date(row.original.exportedAtMillis), "yyyy-MM-dd")}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                exportBatch.mutate(row.original.batchId);
              }}
              disabled={exportBatch.isPending}
            >
              <Download className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Revoke all non-assigned tags in batch ${row.original.batchId}?`)) {
                  revoke.mutate(row.original.batchId);
                }
              }}
              disabled={revoke.isPending}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
    ],
    [exportBatch, revoke],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Tag batches</h1>
        <p className="text-sm text-muted-foreground">
          Pre-generate batches of immutable tag IDs for manufacturing. Tags enter the pool with
          status <code>inventory</code> until allocated to a paid order.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Count">
            <Input
              value={count}
              onChange={(e) => setCount(e.target.value)}
              type="number"
              min={1}
              max={5000}
              className="w-32"
            />
          </Field>
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value as PhysicalTagType)}>
              {TAG_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex-1 min-w-[200px]">
            <Field label="Notes (optional)">
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. printer-A run 2026-05-17"
              />
            </Field>
          </div>
          <Button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
          >
            <Plus className="mr-1 h-4 w-4" />
            {generate.isPending ? "Generating…" : "Generate batch"}
          </Button>
        </CardContent>
      </Card>

      {lastBatch ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="text-sm">
              Generated <strong>{lastBatch.count}</strong> {lastBatch.type} tag(s) in batch{" "}
              <code>{lastBatch.batchId}</code>.
            </div>
            <Button
              size="sm"
              onClick={() => exportBatch.mutate(lastBatch.batchId)}
              disabled={exportBatch.isPending}
            >
              <Download className="mr-1 h-3 w-3" />
              Download CSV
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {actionError ? (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </div>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={!!query.hasNextPage}
        onLoadMore={() => query.fetchNextPage()}
        rowKey={(row) => row.batchId}
        emptyMessage="No batches generated yet. Use the form above to create one."
        {...queryTableProps(query)}
      />
    </div>
  );
}
